import { useCallback, useEffect, useMemo, useState, type KeyboardEvent, type ReactNode } from "react";
import { useNavigate, useParams, useSearchParams } from "react-router-dom";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { ExternalLink, Loader2, RefreshCw } from "lucide-react";

import { QuickBooksTxnLink } from "@/components/hub/QuickBooksTxnLink";
import { MrpLocationChip } from "@/components/mrp/MrpLocationChip";
import { PurchaseOrderDetailLink } from "@/components/hub/HubEntityDetailLink";
import { MegaventoryPurchaseOrderExternalLink } from "@/components/mrp/MegaventoryPurchaseOrderLinks";
import { Badge } from "@/components/ui/Badge";
import { Button } from "@/components/ui/Button";
import { Card, CardBody, CardHeader, CardTitle } from "@/components/ui/Card";
import { HubViewportCard } from "@/components/layout/HubViewportCard";
import { Input } from "@/components/ui/Input";
import { PageTitleWithHelp, ReadOnlyHoverLayer } from "@/components/ui/title-help";
import { ApiError } from "@/lib/api";
import {
  megaventoryNewHandlingInvoiceFromGipiUrl,
  megaventoryPurchaseInvoiceDocumentUrl,
} from "@/lib/megaventoryDocumentUrls";
import { gipiCreatedInCurrentCalendarMonth } from "./gipiCreatedCalendarMonth";
import { formatNumber, formatPoMoney, type MrpLocation } from "@/lib/mrp";
import { useHubModuleWrite } from "@/lib/useHubModuleWrite";
import { cn, EM_DASH } from "@/lib/utils";

const MRP_READ_ONLY_TITLE = "Your role has read-only access to the MRP hub module.";

import {
  cancelPurchaseInvoice,
  fetchPurchaseInvoiceDetail,
  fetchPurchaseInvoiceList,
  filterPurchaseInvoiceListRowsClient,
  purchaseInvoiceDetailQueryKey,
  type PurchaseInvoiceDetailHeader,
  type PurchaseInvoiceListRow,
} from "./purchaseInvoiceApi";

const PAGE_HOVER_HINT =
  "Megaventory goods-in and purchase invoice (GIPI) or tariff handling invoice (TSH) mirrored in the hub. Select a document for read-only detail used in inventory investigation and accounts payable cross-checks.";

function parseMrpLocation(value: string | null | undefined): MrpLocation | null {
  if (value === "suntronic" || value === "kodiak" || value === "sgs") {
    return value;
  }
  return null;
}

function formatShortDate(value: string | null | undefined): string {
  if (value == null || value.trim() === "") {
    return EM_DASH;
  }
  const match = /^(\d{4})-(\d{2})-(\d{2})/.exec(value.trim());
  if (match) {
    const [, yyyy, mm, dd] = match;
    return `${mm}-${dd}-${yyyy}`;
  }
  const parsed = new Date(value);
  if (Number.isNaN(parsed.getTime())) {
    return value;
  }
  return parsed.toLocaleString();
}

function purchaseInvoiceStatusBadge(status: string | null): ReactNode {
  const normalized = status?.trim() ?? "";
  if (normalized === "") {
    return (
      <Badge tone="muted" className="text-[10px]">
        Unknown
      </Badge>
    );
  }
  const lower = normalized.toLowerCase();
  const tone =
    lower === "closed" || lower === "verified"
      ? "success"
      : lower === "pending"
        ? "info"
        : lower === "cancelled" || lower === "canceled"
          ? "danger"
          : "neutral";
  return (
    <Badge tone={tone} className="whitespace-nowrap px-1.5 text-[10px]">
      {normalized}
    </Badge>
  );
}

function PurchaseInvoiceLinesTable({
  lines,
  currency,
}: {
  lines: Array<{
    id: string;
    sku: string;
    description: string | null;
    quantity: number;
    unit_price: number | null;
    line_total: number | null;
  }>;
  currency: string | null | undefined;
}): JSX.Element {
  if (lines.length === 0) {
    return <p className="px-4 py-3 text-sm text-slate-600">No lines on this invoice.</p>;
  }

  const lineTotal = lines.reduce((sum, line) => {
    if (line.line_total != null && Number.isFinite(line.line_total)) {
      return sum + line.line_total;
    }
    const quantity = Number(line.quantity ?? 0);
    const unitPrice = Number(line.unit_price ?? 0);
    if (!Number.isFinite(quantity) || !Number.isFinite(unitPrice)) {
      return sum;
    }
    return sum + quantity * unitPrice;
  }, 0);

  return (
    <div className="overflow-x-auto">
      <table className="w-full min-w-[640px] border-collapse text-left text-sm text-slate-800">
        <thead className="border-b border-slate-200 bg-slate-50/90 text-xs font-semibold uppercase tracking-wide text-slate-500">
          <tr>
            <th className="py-2 pl-4 pr-3">SKU</th>
            <th className="px-3 py-2">Description</th>
            <th className="px-3 py-2 text-right">Qty</th>
            <th className="py-2 pl-3 pr-4 text-right">Unit price</th>
            <th className="py-2 pl-3 pr-4 text-right">Line total</th>
          </tr>
        </thead>
        <tbody className="divide-y divide-slate-100">
          {lines.map((line) => (
            <tr key={line.id}>
              <td className="py-2 pl-4 pr-3 font-mono text-xs">{line.sku?.trim() || EM_DASH}</td>
              <td className="px-3 py-2">{line.description?.trim() || EM_DASH}</td>
              <td className="px-3 py-2 text-right tabular-nums">{formatNumber(line.quantity)}</td>
              <td className="py-2 pl-3 pr-4 text-right tabular-nums">
                {line.unit_price != null ? formatPoMoney(line.unit_price, currency ?? "USD") : EM_DASH}
              </td>
              <td className="py-2 pl-3 pr-4 text-right tabular-nums">
                {line.line_total != null ? formatPoMoney(line.line_total, currency ?? "USD") : EM_DASH}
              </td>
            </tr>
          ))}
        </tbody>
        <tfoot>
          <tr className="border-t border-slate-200 bg-slate-50/60">
            <td
              colSpan={4}
              className="py-2 pl-4 pr-3 text-right text-xs font-semibold uppercase tracking-wide text-slate-500"
            >
              Total
            </td>
            <td className="py-2 pl-3 pr-4 text-right font-semibold tabular-nums text-slate-900">
              {formatPoMoney(lineTotal, currency ?? "USD")}
            </td>
          </tr>
        </tfoot>
      </table>
    </div>
  );
}

function PurchaseInvoiceDetailHeaderCard({
  invoice,
  headerActions,
}: {
  invoice: PurchaseInvoiceDetailHeader;
  headerActions?: ReactNode;
}): JSX.Element {
  const location = parseMrpLocation(invoice.location_code);
  const purchaseOrderId =
    invoice.purchase_order_id != null ? Number.parseInt(invoice.purchase_order_id, 10) : Number.NaN;
  const purchaseOrderMvId =
    invoice.purchase_order_mv_id != null ? Number.parseInt(invoice.purchase_order_mv_id, 10) : Number.NaN;
  const purchaseOrderSystemOfRecord = invoice.purchase_order_system_of_record?.trim().toLowerCase() ?? "";
  const showMegaventoryPurchaseOrderLink =
    Number.isFinite(purchaseOrderMvId) &&
    purchaseOrderMvId > 0 &&
    purchaseOrderSystemOfRecord !== "hub";
  const purchaseInvoiceMvId = invoice.mv_id != null ? Number.parseInt(invoice.mv_id, 10) : Number.NaN;
  const mvNumber = invoice.mv_number?.trim() ?? "";
  const megaventoryUrl =
    Number.isFinite(purchaseInvoiceMvId) && purchaseInvoiceMvId > 0
      ? megaventoryPurchaseInvoiceDocumentUrl(purchaseInvoiceMvId, invoice.mv_number)
      : null;

  return (
    <Card>
      <CardHeader className="flex flex-row flex-wrap items-start justify-between gap-2">
        <div className="space-y-2">
          <div className="flex min-w-0 items-center gap-1.5">
            <CardTitle className="min-w-0 font-mono">{mvNumber !== "" ? mvNumber : "Purchase invoice"}</CardTitle>
            {megaventoryUrl != null ? (
              <a
                href={megaventoryUrl}
                target="_blank"
                rel="noreferrer noopener"
                className="shrink-0 text-brand-600 hover:text-brand-700"
                aria-label={`Open purchase invoice ${mvNumber !== "" ? mvNumber : String(purchaseInvoiceMvId)} in Megaventory`}
              >
                <ExternalLink className="h-3.5 w-3.5" aria-hidden />
              </a>
            ) : null}
          </div>
          <div className="flex flex-wrap items-center gap-2">
            {purchaseInvoiceStatusBadge(invoice.mv_status)}
            {location != null ? <MrpLocationChip code={location} /> : null}
          </div>
        </div>
        {headerActions != null ? <div className="flex flex-wrap items-center gap-2">{headerActions}</div> : null}
      </CardHeader>
      <CardBody className="grid gap-4 lg:grid-cols-3">
        <dl className="space-y-2 text-sm text-slate-800">
          <div>
            <dt className="text-xs font-semibold uppercase tracking-wide text-slate-500">MV document</dt>
            <dd className="mt-0.5 tabular-nums">
              {Number.isFinite(purchaseInvoiceMvId) ? purchaseInvoiceMvId : EM_DASH}
            </dd>
          </div>
          <div>
            <dt className="text-xs font-semibold uppercase tracking-wide text-slate-500">Hub GPO</dt>
            <dd className="mt-0.5">
              {Number.isFinite(purchaseOrderId) ? (
                <span className="inline-flex min-w-0 items-center gap-1">
                  <PurchaseOrderDetailLink
                    purchaseOrderId={purchaseOrderId}
                    purchaseOrderNumber={invoice.purchase_order_number}
                    peek={{ purchase_order_number: invoice.purchase_order_number }}
                  />
                  {showMegaventoryPurchaseOrderLink ? (
                    <MegaventoryPurchaseOrderExternalLink
                      purchaseOrderMvId={purchaseOrderMvId}
                      poNumber={invoice.purchase_order_number}
                    />
                  ) : null}
                </span>
              ) : (
                EM_DASH
              )}
            </dd>
          </div>
          <div>
            <dt className="text-xs font-semibold uppercase tracking-wide text-slate-500">Supplier</dt>
            <dd className="mt-0.5">{invoice.supplier_name ?? EM_DASH}</dd>
          </div>
          <div>
            <dt className="text-xs font-semibold uppercase tracking-wide text-slate-500">Supplier reference</dt>
            <dd className="mt-0.5">{invoice.reference ?? EM_DASH}</dd>
          </div>
          <div>
            <dt className="text-xs font-semibold uppercase tracking-wide text-slate-500">Document type</dt>
            <dd className="mt-0.5">{invoice.document_type?.toUpperCase() ?? EM_DASH}</dd>
          </div>
        </dl>
        <dl className="space-y-2 text-sm text-slate-800">
          <div>
            <dt className="text-xs font-semibold uppercase tracking-wide text-slate-500">Currency</dt>
            <dd className="mt-0.5">{invoice.currency ?? EM_DASH}</dd>
          </div>
          <div>
            <dt className="text-xs font-semibold uppercase tracking-wide text-slate-500">Payment terms</dt>
            <dd className="mt-0.5">{invoice.payment_terms ?? EM_DASH}</dd>
          </div>
          <div>
            <dt className="text-xs font-semibold uppercase tracking-wide text-slate-500">Payment method</dt>
            <dd className="mt-0.5">{invoice.payment_method ?? EM_DASH}</dd>
          </div>
          <div>
            <dt className="text-xs font-semibold uppercase tracking-wide text-slate-500">QuickBooks id</dt>
            <dd className="mt-0.5">
              <QuickBooksTxnLink
                txnId={invoice.qb_id}
                kind="bill"
                documentLabel={mvNumber !== "" ? mvNumber : "this purchase invoice"}
              />
            </dd>
          </div>
        </dl>
        <dl className="space-y-2 text-sm text-slate-800">
          <div>
            <dt className="text-xs font-semibold uppercase tracking-wide text-slate-500">Created</dt>
            <dd className="mt-0.5">{formatShortDate(invoice.created_at)}</dd>
          </div>
          <div>
            <dt className="text-xs font-semibold uppercase tracking-wide text-slate-500">Megaventory updated</dt>
            <dd className="mt-0.5">{formatShortDate(invoice.mv_updated_at)}</dd>
          </div>
        </dl>
      </CardBody>
    </Card>
  );
}

export function PurchaseInvoiceDetailPage(): JSX.Element {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const hubWritesAllowed = useHubModuleWrite("mrp");
  const { id: pathId } = useParams();
  const [searchParams, setSearchParams] = useSearchParams();
  const searchParam = searchParams.get("search") ?? "";
  const idFromQuery = searchParams.get("id") ?? "";
  const [searchText, setSearchText] = useState(() => searchParam);

  const selectedPurchaseInvoiceId = useMemo(() => {
    const raw = (pathId ?? idFromQuery).trim();
    const parsed = Number.parseInt(raw, 10);
    return Number.isFinite(parsed) && parsed > 0 ? parsed : null;
  }, [pathId, idFromQuery]);

  useEffect(() => {
    setSearchText(searchParam);
  }, [searchParam]);

  const listQuery = useQuery<PurchaseInvoiceListRow[], ApiError>({
    queryKey: ["mrp-purchase-invoice", "list", searchParam.trim()],
    queryFn: () => fetchPurchaseInvoiceList(searchParam.trim(), 100),
    staleTime: 30_000,
  });

  const detailQuery = useQuery({
    queryKey:
      selectedPurchaseInvoiceId != null
        ? purchaseInvoiceDetailQueryKey(selectedPurchaseInvoiceId)
        : ["mrp-purchase-invoice", "detail", "none"],
    queryFn: () => fetchPurchaseInvoiceDetail(selectedPurchaseInvoiceId!),
    enabled: selectedPurchaseInvoiceId != null,
  });

  const cancelMutation = useMutation({
    mutationFn: () => {
      if (selectedPurchaseInvoiceId == null) {
        throw new Error("No purchase invoice selected.");
      }
      return cancelPurchaseInvoice(selectedPurchaseInvoiceId);
    },
    onSuccess: async () => {
      await Promise.all([
        queryClient.invalidateQueries({ queryKey: ["mrp-purchase-invoice", "list"] }),
        selectedPurchaseInvoiceId != null
          ? queryClient.invalidateQueries({ queryKey: purchaseInvoiceDetailQueryKey(selectedPurchaseInvoiceId) })
          : Promise.resolve(),
      ]);
    },
  });

  const filteredRows = useMemo(
    () => filterPurchaseInvoiceListRowsClient(listQuery.data ?? [], searchText),
    [listQuery.data, searchText],
  );

  const searchActive = searchText.trim() !== "";

  const openPurchaseInvoice = useCallback(
    (purchaseInvoiceId: number) => {
      const nextSearch = searchParams.get("search");
      const suffix =
        nextSearch != null && nextSearch.trim() !== "" ? `?search=${encodeURIComponent(nextSearch)}` : "";
      navigate(`/mrp/purchase-invoices/${String(purchaseInvoiceId)}${suffix}`, { replace: true });
    },
    [navigate, searchParams],
  );

  function updateSearchFilter(value: string): void {
    setSearchText(value);
    const next = new URLSearchParams(searchParams);
    const trimmed = value.trim();
    if (trimmed === "") {
      next.delete("search");
    } else {
      next.set("search", value);
    }
    if (pathId != null) {
      setSearchParams(next, { replace: true });
      return;
    }
    if (idFromQuery !== "") {
      next.set("id", idFromQuery);
    }
    setSearchParams(next, { replace: true });
  }

  function handleListRowKeyDown(event: KeyboardEvent<HTMLTableRowElement>, purchaseInvoiceId: number): void {
    if (event.key === "Enter" || event.key === " ") {
      event.preventDefault();
      openPurchaseInvoice(purchaseInvoiceId);
    }
  }

  const invoice = detailQuery.data?.purchase_invoice;
  const mvNumber = invoice?.mv_number?.trim() ?? "";
  const isGipiDocument = invoice?.document_type === "gipi";
  const gipiCancelled =
    isGipiDocument &&
    ["cancelled", "canceled"].includes((invoice?.mv_status ?? "").trim().toLowerCase());
  const purchaseInvoiceMvId = invoice?.mv_id != null ? Number.parseInt(invoice.mv_id, 10) : Number.NaN;
  const childTshAllowed =
    invoice?.created_at != null && gipiCreatedInCurrentCalendarMonth(invoice.created_at);
  const createTshMegaventoryUrl =
    childTshAllowed && Number.isFinite(purchaseInvoiceMvId) && purchaseInvoiceMvId > 0
      ? megaventoryNewHandlingInvoiceFromGipiUrl(purchaseInvoiceMvId)
      : null;
  const tabBreadcrumbLabel =
    mvNumber !== ""
      ? mvNumber
      : selectedPurchaseInvoiceId != null
        ? `Purchase invoice #${String(selectedPurchaseInvoiceId)}`
        : "Purchase invoices";
  const megaventoryUrl =
    Number.isFinite(purchaseInvoiceMvId) && purchaseInvoiceMvId > 0
      ? megaventoryPurchaseInvoiceDocumentUrl(purchaseInvoiceMvId, invoice?.mv_number ?? null)
      : null;

  const listCountLabel = (
    <span className="font-normal text-slate-600">
      ({filteredRows.length}
      {searchActive && listQuery.data != null ? ` of ${String(listQuery.data.length)}` : ""})
    </span>
  );

  return (
    <div className="space-y-4">
      <header className="flex flex-col gap-2 sm:flex-row sm:items-start sm:justify-between">
        <PageTitleWithHelp hoverHint={PAGE_HOVER_HINT} breadcrumbLabel={tabBreadcrumbLabel}>
          Purchase invoices
        </PageTitleWithHelp>
        {selectedPurchaseInvoiceId != null ? (
          <div className="flex flex-wrap gap-2">
            {megaventoryUrl != null ? (
              <a
                href={megaventoryUrl}
                target="_blank"
                rel="noreferrer"
                className="inline-flex h-8 items-center rounded-md border border-slate-200 bg-white px-3 text-sm font-medium text-slate-800 hover:bg-slate-50"
              >
                <ExternalLink className="mr-1.5 h-3.5 w-3.5" aria-hidden />
                Open in Megaventory
              </a>
            ) : null}
            <Button
              type="button"
              size="sm"
              variant="secondary"
              onClick={() => void detailQuery.refetch()}
              disabled={detailQuery.isFetching}
            >
              {detailQuery.isFetching ? (
                <Loader2 className="mr-1.5 h-3.5 w-3.5 animate-spin" aria-hidden />
              ) : (
                <RefreshCw className="mr-1.5 h-3.5 w-3.5" aria-hidden />
              )}
              Refresh
            </Button>
          </div>
        ) : null}
      </header>

      <Card>
        <CardHeader>
          <CardTitle>Filters</CardTitle>
        </CardHeader>
        <CardBody>
          <label className="mb-1 block text-xs font-medium text-slate-700" htmlFor="purchase-invoice-search">
            Search purchase invoices
          </label>
          <Input
            id="purchase-invoice-search"
            type="search"
            className="h-10"
            placeholder="Hub id, MV number, supplier, purchase order, reference…"
            value={searchText}
            onChange={(event) => updateSearchFilter(event.target.value)}
          />
        </CardBody>
      </Card>

      <div className="grid min-h-[28rem] gap-4 lg:grid-cols-[minmax(20rem,26rem)_minmax(0,1fr)] lg:items-stretch">
        <HubViewportCard>
          <CardHeader className="flex shrink-0 flex-row flex-wrap items-center justify-between gap-2">
            <CardTitle>Purchase invoices {listCountLabel}</CardTitle>
            {listQuery.isFetching ? <Loader2 className="h-4 w-4 animate-spin text-slate-500" aria-hidden /> : null}
          </CardHeader>
          <CardBody className="flex min-h-0 flex-1 flex-col p-0">
            {listQuery.isError ? (
              <p className="mx-5 my-4 rounded-md border border-rose-200 bg-rose-50 px-3 py-2 text-sm text-rose-800">
                {listQuery.error.message}
              </p>
            ) : null}
            {listQuery.isLoading ? (
              <p className="px-5 py-4 text-sm text-slate-600">Loading purchase invoices…</p>
            ) : null}
            {listQuery.data != null && filteredRows.length === 0 && !listQuery.isLoading ? (
              <p className="px-5 py-4 text-sm text-slate-600">No purchase invoices match these filters.</p>
            ) : null}
            {filteredRows.length > 0 ? (
              <div className="min-h-0 flex-1 overflow-y-auto">
                <table className="w-full table-fixed border-collapse text-left text-xs text-slate-800">
                  <colgroup>
                    <col className="w-[5.5rem]" />
                    <col />
                    <col className="w-[5.5rem]" />
                  </colgroup>
                  <thead className="sticky top-0 z-10 border-b border-slate-200 bg-white text-[11px] font-semibold uppercase tracking-wide text-slate-600 shadow-[0_1px_0_0_rgb(226_232_240)]">
                    <tr>
                      <th className="px-3 py-2">Invoice</th>
                      <th className="px-2 py-2">Purchase order</th>
                      <th className="px-3 py-2">Created</th>
                    </tr>
                  </thead>
                  <tbody>
                    {filteredRows.map((row) => {
                      const rowId = Number.parseInt(row.id, 10);
                      const isSelected = selectedPurchaseInvoiceId === rowId;
                      return (
                        <tr
                          key={row.id}
                          role="button"
                          tabIndex={0}
                          aria-selected={isSelected}
                          onClick={() => openPurchaseInvoice(rowId)}
                          onKeyDown={(event) => handleListRowKeyDown(event, rowId)}
                          className={cn(
                            "cursor-pointer border-b border-slate-50 last:border-none hover:bg-slate-50/90",
                            isSelected && "bg-sky-50 ring-1 ring-inset ring-sky-200",
                          )}
                        >
                          <td className="px-3 py-2 align-top">
                            <div className="font-mono tabular-nums font-medium text-slate-900">
                              {row.mv_number?.trim() || `#${row.id}`}
                            </div>
                            <div className="mt-0.5">{purchaseInvoiceStatusBadge(row.mv_status)}</div>
                          </td>
                          <td className="px-2 py-2 align-top">
                            <div className="truncate font-medium text-slate-900">
                              {row.purchase_order_number ?? EM_DASH}
                            </div>
                            {row.supplier_name ? (
                              <div className="truncate text-[11px] text-slate-500">{row.supplier_name}</div>
                            ) : null}
                          </td>
                          <td className="px-3 py-2 align-top whitespace-nowrap tabular-nums">
                            {formatShortDate(row.created_at)}
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            ) : null}
          </CardBody>
        </HubViewportCard>

        <div className="flex min-h-0 min-w-0 flex-col gap-4">
          {selectedPurchaseInvoiceId == null ? (
            <Card>
              <CardBody>
                <p className="text-sm text-slate-600">
                  Select a purchase invoice from the list or follow a GIPI or TSH link from inventory discrepancies or
                  another hub page.
                </p>
              </CardBody>
            </Card>
          ) : detailQuery.isLoading ? (
            <Card>
              <CardBody>
                <div className="flex items-center gap-2 text-sm text-slate-500">
                  <Loader2 className="h-4 w-4 animate-spin" aria-hidden />
                  Loading purchase invoice…
                </div>
              </CardBody>
            </Card>
          ) : detailQuery.isError ? (
            <Card>
              <CardBody>
                <p className="text-sm text-red-700">
                  {detailQuery.error instanceof Error ? detailQuery.error.message : String(detailQuery.error)}
                </p>
              </CardBody>
            </Card>
          ) : invoice != null && detailQuery.data != null ? (
            <>
              {cancelMutation.isError ? (
                <p className="text-sm text-rose-700">
                  {cancelMutation.error instanceof Error
                    ? cancelMutation.error.message
                    : String(cancelMutation.error)}
                </p>
              ) : null}
              <PurchaseInvoiceDetailHeaderCard
                invoice={invoice}
                headerActions={
                  isGipiDocument ? (
                    <div className="flex flex-wrap items-center gap-2">
                      {gipiCancelled ? null : (
                        <ReadOnlyHoverLayer
                          active={!hubWritesAllowed}
                          hoverHint={MRP_READ_ONLY_TITLE}
                          layout="inline"
                        >
                          <ReadOnlyHoverLayer
                            active={hubWritesAllowed && invoice.can_cancel !== true}
                            hoverHint={
                              invoice.cancel_blocked_reason ?? "This GIPI cannot be cancelled."
                            }
                            layout="inline"
                          >
                            <Button
                              type="button"
                              variant="danger"
                              disabled={
                                !hubWritesAllowed ||
                                invoice.can_cancel !== true ||
                                cancelMutation.isPending
                              }
                              isLoading={cancelMutation.isPending}
                              onClick={() => {
                                const label = mvNumber !== "" ? mvNumber : "this GIPI";
                                if (
                                  !window.confirm(
                                    `Cancel ${label}? This cancels the Megaventory document and cannot be undone from the hub.`,
                                  )
                                ) {
                                  return;
                                }
                                cancelMutation.mutate();
                              }}
                            >
                              Cancel
                            </Button>
                          </ReadOnlyHoverLayer>
                        </ReadOnlyHoverLayer>
                      )}
                      <ReadOnlyHoverLayer
                        active={!hubWritesAllowed}
                        hoverHint={MRP_READ_ONLY_TITLE}
                        layout="inline"
                      >
                        <ReadOnlyHoverLayer
                          active={hubWritesAllowed && gipiCancelled}
                          hoverHint="Cannot create a TSH for a cancelled GIPI."
                          layout="inline"
                        >
                          <ReadOnlyHoverLayer
                            active={hubWritesAllowed && !gipiCancelled && !childTshAllowed}
                            hoverHint="A child TSH can only be created in the calendar month the GIPI was created in Megaventory. The vendor invoice TSH workflow creates a GIPI for these charges instead."
                            layout="inline"
                          >
                          <ReadOnlyHoverLayer
                            active={hubWritesAllowed && !gipiCancelled && childTshAllowed && createTshMegaventoryUrl == null}
                            hoverHint="This GIPI is missing a Megaventory document id."
                            layout="inline"
                          >
                            {createTshMegaventoryUrl != null && hubWritesAllowed && !gipiCancelled ? (
                              <a
                                href={createTshMegaventoryUrl}
                                target="_blank"
                                rel="noreferrer noopener"
                                className="inline-flex h-10 items-center justify-center gap-2 rounded-md bg-brand-600 px-4 text-sm font-medium text-white transition-colors hover:bg-brand-700 focus:outline-none focus:ring-2 focus:ring-brand-500 focus:ring-offset-2"
                                aria-label={`Create TSH for ${mvNumber !== "" ? mvNumber : "this GIPI"} in Megaventory`}
                              >
                                <ExternalLink className="h-3.5 w-3.5" aria-hidden />
                                Create TSH
                              </a>
                            ) : (
                              <Button type="button" disabled>
                                Create TSH
                              </Button>
                            )}
                          </ReadOnlyHoverLayer>
                          </ReadOnlyHoverLayer>
                        </ReadOnlyHoverLayer>
                      </ReadOnlyHoverLayer>
                    </div>
                  ) : null
                }
              />
              <Card>
                <CardHeader>
                  <CardTitle>Line items</CardTitle>
                </CardHeader>
                <CardBody className="p-0">
                  <PurchaseInvoiceLinesTable lines={detailQuery.data.lines} currency={invoice.currency} />
                </CardBody>
              </Card>
            </>
          ) : null}
        </div>
      </div>
    </div>
  );
}
